import dayjs from 'dayjs';
import { isCatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportResult } from '@/api/models';
import {
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import type {
    CatalogParseProgress,
    CatalogWorkbookRefusal,
} from '@/pages/catalogImport/parse';
import type {
    CatalogImportFile,
    PreviewStatus,
} from '@/pages/catalogImport/store';
import {
    TEMPLATE_FIELD_COLUMNS,
    type TemplateLanguage,
} from '@/pages/catalogImport/template';
import type { FileRefusal } from './uploadIntake';

/**
 * What the Upload step shows for the file, worked out from the step's own run
 * and the import store. Pure, so every state is tested without the worker or
 * the API.
 */

/** Why the file cannot go on to Names. The View puts each into words. */
export type UploadBlock =
    | { kind: 'tooManyRows'; rowCount: number; limit: number }
    /** The missing headers, written in the UI language. */
    | { kind: 'missingHeaders'; headers: string[] }
    | { kind: 'noProductsSheet' }
    /** Not a workbook exceljs can open, or the worker broke. */
    | { kind: 'unreadable' }
    /** A Products sheet with no data rows. */
    | { kind: 'empty' }
    /** The preview's header problems, or a refusal of the whole request. */
    | { kind: 'notices'; notices: CatalogImportNotice[] }
    /** The check did not answer (connection, an unknown error): Retry. */
    | { kind: 'checkFailed' };

/** What the step points out but lets through. */
export type UploadWarnings = {
    ignoredColumns: string[];
    /** The same file (by hash) imported before: when (`DD/MM/YYYY`) and where. */
    previousImport: { importedOn: string; branchName: string } | null;
};

/** The drop zone's side of the step, from choosing a file to its check. */
export type UploadIntakeState =
    | { status: 'choosing'; refusal: FileRefusal | null }
    | {
          status: 'reading';
          fileName: string;
          /** `null` until the worker's first report: the bar is indeterminate. */
          progress: CatalogParseProgress | null;
      }
    | { status: 'checking'; fileName: string; rowCount: number }
    | {
          status: 'blocked';
          fileName: string;
          /** `null` when the file was not read far enough to count. */
          rowCount: number | null;
          block: UploadBlock;
          warnings: UploadWarnings;
          /** A file dropped over this one and refused; absent otherwise. */
          refusal?: FileRefusal;
      }
    | {
          status: 'checked';
          fileName: string;
          rowCount: number;
          warnings: UploadWarnings;
          /** A file dropped over this one and refused; absent otherwise. */
          refusal?: FileRefusal;
      };

/** The step's own part: the file it is reading, or the reason it stopped before the store. */
export type UploadRun =
    | { phase: 'reading'; file: File }
    | { phase: 'stopped'; file: File; block: UploadBlock };

export const NO_WARNINGS: UploadWarnings = {
    ignoredColumns: [],
    previousImport: null,
};

/** The parse's refusal, as the step blocks on it. */
export const blockOfRefusal = (
    refusal: CatalogWorkbookRefusal,
    language: TemplateLanguage,
): UploadBlock => {
    switch (refusal.refused) {
        case 'TOO_MANY_ROWS':
            return {
                kind: 'tooManyRows',
                rowCount: refusal.rowCount,
                limit: refusal.limit,
            };
        case 'MISSING_HEADERS':
            return {
                kind: 'missingHeaders',
                headers: refusal.missing.map(
                    (key) =>
                        TEMPLATE_FIELD_COLUMNS.find(
                            (column) => column.key === key,
                        )?.header[language] ?? key,
                ),
            };
        case 'NO_PRODUCTS_SHEET':
            return { kind: 'noProductsSheet' };
        case 'PARSE_FAILED':
            return { kind: 'unreadable' };
    }
};

/**
 * A refused preview: a code of the catalog import has its sentence; anything
 * else (no answer, a code this import does not use) is worth a retry.
 */
export const blockOfPreviewError = (
    error: ErrorResponse | null,
): UploadBlock =>
    error?.code !== undefined && isCatalogImportCode(error.code)
        ? { kind: 'notices', notices: [noticeFor({ code: error.code })] }
        : { kind: 'checkFailed' };

/** The warnings of a parsed file and its preview. */
export const warningsOf = (
    file: Pick<CatalogImportFile, 'ignoredColumns'>,
    preview: CatalogImportResult | null,
): UploadWarnings => {
    const previous = preview?.header.previousImport ?? null;
    return {
        ignoredColumns: file.ignoredColumns,
        previousImport: previous
            ? {
                  // The day it happened where the reader is.
                  importedOn: dayjs(previous.createdAt).format('DD/MM/YYYY'),
                  branchName: previous.branchName,
              }
            : null,
    };
};

export const hasWarnings = (warnings: UploadWarnings): boolean =>
    warnings.ignoredColumns.length > 0 || warnings.previousImport !== null;

/**
 * The file is being read or checked: Cancel is the step's only action, and
 * Back and Continue are shown disabled.
 */
export const isUploadBusy = (intake: UploadIntakeState): boolean =>
    intake.status === 'reading' || intake.status === 'checking';

export type UploadIntakeInput = {
    run: UploadRun | null;
    /** The last refusal, of a file picked or dropped on the drop zone or over the read file. */
    refusal: FileRefusal | null;
    progress: CatalogParseProgress | null;
    file: CatalogImportFile | null;
    preview: CatalogImportResult | null;
    previewStatus: PreviewStatus;
    /** Why the last preview was refused, when this step sent it. */
    previewError: ErrorResponse | null;
};

/**
 * The step's run comes first (a file being read, or stopped before the
 * store); then the store's file and its preview; else the drop zone. A file
 * in the store is checking until its preview answers, so coming back to
 * Upload shows the file as it stands. A refusal shows on the drop zone, and
 * on a blocked or checked file's card, which takes a drop too.
 */
export const uploadIntakeState = ({
    run,
    refusal,
    progress,
    file,
    preview,
    previewStatus,
    previewError,
}: UploadIntakeInput): UploadIntakeState => {
    if (run?.phase === 'reading') {
        return { status: 'reading', fileName: run.file.name, progress };
    }
    const dropped = refusal ? { refusal } : {};
    if (run?.phase === 'stopped') {
        return {
            status: 'blocked',
            fileName: run.file.name,
            rowCount:
                run.block.kind === 'tooManyRows' ? run.block.rowCount : null,
            block: run.block,
            warnings: NO_WARNINGS,
            ...dropped,
        };
    }
    if (!file) {
        return { status: 'choosing', refusal };
    }

    const base = { fileName: file.name, rowCount: file.rowCount };
    if (previewStatus === 'failed') {
        return {
            status: 'blocked',
            ...base,
            block: blockOfPreviewError(previewError),
            warnings: warningsOf(file, null),
            ...dropped,
        };
    }
    if (previewStatus !== 'done' || !preview) {
        return { status: 'checking', ...base };
    }

    const warnings = warningsOf(file, preview);
    const headerProblems = preview.header.problems;
    return headerProblems.length > 0
        ? {
              status: 'blocked',
              ...base,
              block: {
                  kind: 'notices',
                  notices: headerProblems.map((problem) => noticeFor(problem)),
              },
              warnings,
              ...dropped,
          }
        : { status: 'checked', ...base, warnings, ...dropped };
};
